import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';

export const useDateManager = () => {
  const [fromDate, setFromDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [tempFromDate, setTempFromDate] = useState<string>("");
  const [tempToDate, setTempToDate] = useState<string>("");
  const [weeks, setWeeks] = useState<string[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<string>("");

  // Helper function to calculate the start of the week (Sunday)
  const getWeekStartDate = useCallback((date: Date): string => {
    const day = date.getDay();
    const diff = date.getDate() - day;
    const startOfWeek = new Date(date);
    startOfWeek.setDate(diff);
    return startOfWeek.toISOString().split("T")[0];
  }, []);

  // Helper function to calculate the end of the week (Saturday)
  const getWeekEndDate = useCallback((date: Date): string => {
    const day = date.getDay();
    const diff = date.getDate() - day + 6;
    const endOfWeek = new Date(date);
    endOfWeek.setDate(diff);
    return endOfWeek.toISOString().split("T")[0];
  }, []);

  // Helper function to get the week range (start and end dates)
  const getWeekRange = useCallback((date: Date): string => {
    const startOfWeek = getWeekStartDate(date);
    const endOfWeek = getWeekEndDate(date);
    return `${startOfWeek} to ${endOfWeek}`;
  }, [getWeekStartDate, getWeekEndDate]);

  // Generate all past weeks up to today
  const generatePastWeeks = useCallback(() => {
    const today = new Date();
    const startDate = new Date("2024-01-01");
    const weeksList: string[] = [];

    let currentWeek = new Date(today);
    while (currentWeek >= startDate) {
      weeksList.push(getWeekRange(currentWeek));
      currentWeek.setDate(currentWeek.getDate() - 7);
    }

    setWeeks(weeksList);
    setSelectedWeek(weeksList[0]);
  }, [getWeekRange]);

  // Set default dates (current week) on page load
  useEffect(() => {
    const today = new Date();
    const from = getWeekStartDate(today);
    const to = getWeekEndDate(today);

    setFromDate(from);
    setToDate(to);
    generatePastWeeks();
  }, [getWeekStartDate, getWeekEndDate, generatePastWeeks]);

  // Handle week selection
  const handleWeekChange = useCallback((selectedRange: string) => {
    setSelectedWeek(selectedRange);
    const [from, to] = selectedRange.split(" to ");
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle manual date change
  const handleDateChange = useCallback(() => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
  }, [tempFromDate, tempToDate]);

  return {
    fromDate,
    toDate,
    tempFromDate,
    tempToDate,
    weeks,
    selectedWeek,
    setTempFromDate,
    setTempToDate,
    handleWeekChange,
    handleDateChange,
  };
};